import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { http, formatDateTime } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { ChatCircle, Phone, EnvelopeSimple, Note } from "@phosphor-icons/react";
import SearchableContactSelect from "@/components/SearchableContactSelect";

const icon = { note: Note, call: Phone, email: EnvelopeSimple, sms: ChatCircle };

export default function ActivitiesPage() {
  const { t } = useI18n();
  const [activities, setActivities] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [form, setForm] = useState({ type: "note", body: "", contact_id: "", deal_id: "" });

  const load = async () => {
    const [a, c, d] = await Promise.all([
      http.get("/activities"), http.get("/contacts"), http.get("/deals")
    ]);
    setActivities(a.data);
    setContacts(c.data);
    setDeals(d.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.body.trim()) return;
    const payload = { ...form };
    if (!payload.contact_id) delete payload.contact_id;
    if (!payload.deal_id) delete payload.deal_id;
    try {
      await http.post("/activities", payload);
      setForm({ type: "note", body: "", contact_id: "", deal_id: "" });
      toast.success("Aktivitas ditambahkan");
      load();
    } catch { toast.error("Gagal menambah aktivitas"); }
  };

  const contactMap = contacts.reduce((a, c) => ({ ...a, [c.id]: c.name }), {});
  const dealMap = deals.reduce((a, d) => ({ ...a, [d.id]: d.title }), {});

  return (
    <AppLayout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-8">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#64748B] font-bold">Feed</div>
          <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl tracking-tight mt-1 text-[#173042]">Aktivitas</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6">
        <div className="lg:col-span-2 order-2 lg:order-1">
          <div className="border-l-2 border-[#E2E8F0] pl-4 sm:pl-6 space-y-4 sm:space-y-6" data-testid="activities-feed">
            {activities.length === 0 && <div className="text-xs text-[#64748B] py-8 text-center bg-white rounded-xl border border-[#E2E8F0] shadow-2xs">Belum ada aktivitas.</div>}
            {activities.map((a) => {
              const Icon = icon[a.type] || Note;
              return (
                <div key={a.id} className="relative" data-testid={`activity-${a.id}`}>
                  <div className="absolute -left-[25px] sm:-left-[35px] top-1 w-6 h-6 rounded-full bg-white border-2 border-[#173B4D] flex items-center justify-center text-[#173B4D] shadow-2xs">
                    <Icon size={12} weight="bold" />
                  </div>
                  <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 shadow-2xs">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0F7180] bg-[#0F7180]/10 px-2 py-0.5 rounded">{a.type}</div>
                      <div className="text-[10px] font-mono-data text-[#64748B]">{formatDateTime(a.created_at)}</div>
                    </div>
                    <div className="text-xs sm:text-sm text-[#173042] mt-2 leading-relaxed font-medium">{a.body}</div>
                    <div className="mt-2.5 pt-2 border-t border-[#F1F5F9] flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-mono-data text-[#64748B]">
                      {a.deal_id && dealMap[a.deal_id] && <span>Deal: <strong className="text-[#173042]">{dealMap[a.deal_id]}</strong></span>}
                      {a.contact_id && contactMap[a.contact_id] && <span>Kontak: <strong className="text-[#173042]">{contactMap[a.contact_id]}</strong></span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 sm:p-6 shadow-2xs sticky top-20">
            <h3 className="font-bold text-base text-[#173042] mb-1">Log Aktivitas</h3>
            <p className="text-xs text-[#64748B] mb-4">Catat interaksi terbaru dengan prospek</p>
            <form onSubmit={submit} className="space-y-3.5" data-testid="activity-form">
              <div>
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  {["note", "call", "email", "sms"].map((tp) => (
                    <button
                      type="button"
                      key={tp}
                      onClick={() => setForm({ ...form, type: tp })}
                      data-testid={`activity-type-${tp}`}
                      className={`px-3 py-1 text-[10px] uppercase tracking-[0.1em] font-bold rounded-lg border transition-colors ${
                        form.type === tp ? "bg-[#173B4D] text-white border-[#173B4D] shadow-2xs" : "border-[#CBD5E1] text-[#64748B] hover:border-[#173B4D]"
                      }`}
                    >
                      {tp}
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid="activity-body"
                  required
                  placeholder="Catat hasil call, email, catatan meeting..."
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  className="w-full rounded-lg border border-[#CBD5E1] p-3 text-xs text-[#173042] placeholder:text-slate-400 focus:outline-none focus:border-[#173B4D] min-h-[90px]"
                />
              </div>
              <select value={form.deal_id} onChange={(e) => setForm({ ...form, deal_id: e.target.value })} className="w-full rounded-lg border border-[#CBD5E1] p-2 text-xs text-[#173042] focus:outline-none focus:border-[#173B4D]">
                <option value="">Tautkan ke deal (opsional)</option>
                {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
              </select>
              <SearchableContactSelect
                contacts={contacts}
                value={form.contact_id}
                onChange={(id) => setForm({ ...form, contact_id: id })}
                placeholder="Tautkan ke kontak (opsional)"
              />
              <button type="submit" data-testid="activity-submit" className="w-full bg-[#173B4D] hover:bg-[#112B38] text-white py-2.5 rounded-lg text-xs font-semibold uppercase tracking-wider shadow-xs transition-colors">
                Simpan Aktivitas
              </button>
            </form>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
